import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { ToastContainer } from 'react-toastify';
import Avatar from './Avatar/Avatar';
import Button from './Button/Button';
import {
  AlertHexagonFilledIcon,
  AlertTriangleFilledIcon,
  CheckCircleFilledIcon,
  InfoCircleFilledIcon,
} from './icons';

const ICONS = {
  success: CheckCircleFilledIcon,
  error: AlertHexagonFilledIcon,
  info: InfoCircleFilledIcon,
  warn: AlertTriangleFilledIcon,
  loading: () => <CircularProgress size={20} />,
};

const Toast = ({
  message,
  title,
  type,
  customButton,
  customIcon,
  closeToast,
}) => {
  const themeVar =
    type === 'warn' ? 'warning' : type === 'info' ? 'primary' : type;
  const Icon = ICONS[type] ?? ICONS['info'];

  let icon = <Icon sx={{ color: themeVar + '.subtle' }} />;
  let button = (
    <Button onClick={closeToast} color={themeVar}>
      Close
    </Button>
  );
  if (customButton) {
    button = customButton;
  } else if (type === 'success') {
    button = (
      <Button onClick={closeToast} color={themeVar}>
        Ok, great
      </Button>
    );
  } else if (type === 'error') {
    button = (
      <Button onClick={closeToast} color={themeVar}>
        Ok
      </Button>
    );
  } else if (type === 'warn') {
    button = (
      <Button onClick={closeToast} color={themeVar}>
        Ok
      </Button>
    );
  }
  if (customIcon) {
    icon = customIcon;
  }

  return (
    <Stack
      sx={{
        bgcolor: themeVar + '.subtle',
        border: `1px solid`,
        borderColor: themeVar + '.subtleBorder',
        px: 2,
        py: 1.5,
        borderRadius: 1,
      }}
      direction="row"
      spacing={1}
      flex={1}
    >
      <Avatar sx={{ bgcolor: themeVar + '.main' }} size="large">
        {icon}
      </Avatar>
      <Box flex={1}>
        <Stack mb={1}>
          {title && (
            <Typography
              color={`${themeVar}.subtleContrastText`}
              fontWeight={600}
            >
              {title}
            </Typography>
          )}
          <Typography color={`${themeVar}.subtleContrastTextMuted`}>
            {message}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          {button}
        </Stack>
      </Box>
    </Stack>
  );
};

Toast.propTypes = {
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  type: PropTypes.oneOf(['success', 'error', 'warn', 'info']),
};

export const Snack = ({ message, type, iconProps = {} }) => {
  const Icon = ICONS[type];
  const themeVar =
    type === 'warn' ? 'warning' : type === 'info' ? 'primary' : type;

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1}
      sx={{
        flex: 1,
        p: 1.5,
        borderRadius: 0.8,
        bgcolor: themeVar + '.subtle',
        border: `1px solid`,
        borderColor: themeVar + '.subtleBorder',
      }}
    >
      <Icon color={themeVar} />
      <Typography sx={{ color: themeVar + '.subtleContrastTextMuted' }}>
        {message}
      </Typography>
    </Stack>
  );
};

export default Toast;

export const CustomToastContainer = styled(ToastContainer)(
  ({}) => `
  .Toastify__toast {
    padding: 0;
    min-height: unset;
  }
  .Toastify__toast-theme--dark {
    background: transparent;
  }
  .Toastify__toast-theme--light {
    background: transparent;
  }
 `,
);
